import { appFetch } from 'api/appFetch';
import React, { createContext, useState } from 'react';
import { useRef } from 'react';
import { useEffect } from 'react';
import * as io from 'socket.io-client';
import { v4 as uuid } from 'uuid';

interface Props {
  children: React.ReactNode;
}

const SOCKET_SERVER_URL =
  import.meta.env.REACT_APP_SOCKET_SERVER_URL ||
  import.meta.env.REACT_APP_API_SERVER_URL ||
  '';

export const SocketContext = createContext<{
  socketId: string | null;
  socket: SocketIOClient.Socket | null;
}>({
  socketId: null,
  socket: null,
});

export default function SocketProvider({ children }: Props) {
  const [socketId, setSocketId] = useState<string | null>(null);
  const socketRef = useRef<SocketIOClient.Socket | null>(null);

  useEffect(() => {
    const connectSocket = async () => {
      socketRef.current = io.connect(SOCKET_SERVER_URL);
      const socketId = uuid();
      const socket = socketRef.current;

      socket.on('connect', () => {
        authenticateSocket(socket, socketId);
        setSocketId(socketId);
      });

      socket.on('disconnect', () => {
        setSocketId(null);
        socket.open();
      });

      socket.on('reconnect', () => {
        authenticateSocket(socket, socketId);
      });
    };

    connectSocket();
  }, []);

  return (
    <SocketContext.Provider value={{ socketId, socket: socketRef.current }}>
      {children}
    </SocketContext.Provider>
  );
}

// We fetch a short-lived JWT from the API server containing the user's
// current account, and then we pass that to the socket server to subscribe
// to that account's room.
async function authenticateSocket(
  socket: SocketIOClient.Socket,
  socketId: string
) {
  const jwt = await fetchJwt();
  if (!jwt) {
    console.warn('Failed to fetch Socket JWT');
    return;
  }
  socket.emit('authenticate', { jwt, uuid: socketId });
}

async function fetchJwt(): Promise<string | null> {
  try {
    const res = await appFetch('/users/socket-token');
    if (!res.ok) {
      console.error('Failed to fetch socket token');
      return null;
    }
    const { token } = await res.json();
    return token;
  } catch (e) {
    console.error('Failed to fetch socket token', e);
    return null;
  }
}
